import type {
  AuditEvent,
  Conversation,
  KnowledgeDocument,
  Memory,
  Message,
  ModelRegistryEntry,
  PendingApproval,
  RunDetail,
  SessionInfo,
  StorageUsage,
  ToolDescriptor,
  UsageSummary
} from '@ralph/contracts'
import { SUPPORTED_UPLOAD_FORMATS } from '@ralph/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend, setCsrfToken } from '@/api/client.ts'

type Page<T> = { items: T[]; nextCursor: string | null }

export const queryKeys = {
  session: ['session'] as const,
  conversations: ['conversations'] as const,
  messages: (id: string) => ['messages', id] as const,
  run: (id: string) => ['run', id] as const,
  runs: ['runs'] as const,
  approvals: ['approvals'] as const,
  memories: ['memories'] as const,
  tools: ['admin', 'tools'] as const,
  models: ['admin', 'models'] as const,
  usage: (days: number) => ['admin', 'usage', days] as const,
  storage: ['admin', 'storage'] as const,
  audit: ['admin', 'audit'] as const
}

/**
 * The session query is the app's root dependency: routing, the CSRF token and every
 * role-gated affordance hang off it. It refetches on window focus so a session revoked in
 * another tab surfaces here rather than as a cascade of 401s.
 */
const sessionQuery = {
  queryKey: queryKeys.session,
  queryFn: async () => {
    const session = await apiGet<SessionInfo>('/api/auth/session')
    setCsrfToken(session.csrfToken)
    return session
  },
  staleTime: 60_000,
  retry: false
} as const

export const useSession = () => useQuery({ ...sessionQuery, refetchOnWindowFocus: true })

/**
 * The session as somebody else has already fetched it — never a fetch of its own.
 *
 * For anything mounted ABOVE the router that wants to know who is signed in without changing
 * when the session is read. `enabled: false` makes this observer passive: it receives whatever
 * `RequireAuth` fetches and whatever a mutation writes into the cache, and it never starts a
 * request.
 *
 * That restraint is load-bearing, and it was learned the hard way. The language sync first
 * used `useSession` from the top of the tree, which put a session read in flight on the
 * SIGN-IN page — where nothing had ever read it before. Sign in before that read returned,
 * and the app bounced straight back to `/login` (see `useLogin`).
 *
 * It has a cost that was learned the same way, a few hours later: a passive observer does not
 * make a query "active", so `invalidateQueries` refetches nothing on its account. Anything
 * that needs the session to be CURRENT while only this is mounted has to fetch it outright.
 */
export const useSessionSnapshot = () => useQuery({ ...sessionQuery, enabled: false })

export const useLogin = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      email: string
      password: string
      displayName?: string
      roles?: string[]
      groups?: string[]
    }) => apiSend<{ authenticated: boolean; csrfToken: string }>('/api/auth/local', 'POST', body),
    onSuccess: async (data) => {
      setCsrfToken(data.csrfToken)
      // Cancel FIRST. A session read still in flight from before the cookie existed must not
      // be allowed to answer for after it — and `invalidateQueries` alone lets it: when a
      // query has no data yet, TanStack does not cancel-and-refetch, it JOINS the fetch
      // already in flight. That fetch was sent without a cookie, comes back
      // `authenticated: false`, lands in the cache as fresh, and `RequireAuth` bounces a
      // person who has just signed in straight back to the sign-in page with an empty form.
      // It only happens when the first read is slower than the person — a cold server, a
      // slow connection — which is why it survived every warm test run.
      await client.cancelQueries({ queryKey: queryKeys.session })

      // Then READ the session; do not merely invalidate it. Invalidating marks the entry stale
      // and leaves the reading to whoever is observing it, and on the sign-in page nobody is:
      // the one observer up here is `useSessionSnapshot`, which is passive on purpose. So the
      // cache went on saying what it learned on the way in — a person who opens any protected
      // page signed out is sent here by `RequireAuth`, which has by then cached
      // `authenticated: false` — and the `navigate()` that follows this handed that answer
      // straight back to the same guard. Signed in, cookie set, and returned to an empty form;
      // the second attempt worked, because the bounce itself had triggered the read. Every
      // browser journey opened `/login` directly, with nothing cached, and never saw it.
      //
      // `staleTime: 0` so this is a request and not a cache hit, and awaited so the answer is
      // in the cache before the caller navigates on it.
      try {
        await client.fetchQuery({ ...sessionQuery, staleTime: 0 })
      } catch {
        // Signing in worked; only the read after it failed. Forget what the cache believes
        // rather than leave "not signed in" standing, and the guard will ask for itself.
        await client.resetQueries({ queryKey: queryKeys.session })
      }
    }
  })
}

export const useLogout = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => apiSend<{ signedOut: boolean }>('/api/auth/logout', 'POST'),
    onSuccess: () => {
      setCsrfToken(null)
      client.clear()
    }
  })
}

export const useConversations = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.conversations,
    queryFn: () => apiGet<Page<Conversation>>('/api/conversations'),
    enabled,
    staleTime: 15_000
  })

/**
 * Starts a conversation, optionally with a chosen agent.
 *
 * `agentId` is the client half of AGM-007: the API has always accepted it and the web app
 * never sent one, so every conversation on this deployment silently pinned `default` (F-10).
 * It stays optional — the server still falls back to the deployment's default agent — but
 * every caller that knows which agent the person picked must pass it, because the pin is
 * made at creation and cannot be changed afterwards.
 */
export const useCreateConversation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input?: { title?: string; agentId?: string }) =>
      apiSend<Conversation>('/api/conversations', 'POST', {
        ...(input?.title ? { title: input.title } : {}),
        ...(input?.agentId ? { agentId: input.agentId } : {})
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.conversations })
  })
}

export const useUpdateConversation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; title?: string; pinned?: boolean; archived?: boolean }) =>
      apiSend<Conversation>(`/api/conversations/${id}`, 'PATCH', patch),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.conversations })
  })
}

export const useDeleteConversation = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiSend<{ deleted: boolean }>(`/api/conversations/${id}`, 'DELETE'),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.conversations })
  })
}

export const useMessages = (conversationId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.messages(conversationId ?? ''),
    queryFn: () => apiGet<Page<Message>>(`/api/conversations/${conversationId}/messages`),
    enabled: Boolean(conversationId),
    staleTime: 0
  })

export const useRun = (runId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.run(runId ?? ''),
    queryFn: () => apiGet<RunDetail>(`/api/runs/${runId}`),
    enabled: Boolean(runId)
  })

export const useRuns = () =>
  useQuery({
    queryKey: queryKeys.runs,
    queryFn: () =>
      apiGet<
        Page<{
          id: string
          conversationId: string
          conversationTitle: string
          status: string
          createdAt: string
          endedAt: string | null
          modelCalls: number
          toolCalls: number
          costUsd: number
        }>
      >('/api/runs'),
    staleTime: 10_000
  })

export const useApprovals = () =>
  useQuery({
    queryKey: queryKeys.approvals,
    queryFn: () => apiGet<Page<PendingApproval>>('/api/approvals'),
    // Approvals block a run and can arrive while the user is elsewhere in the app, so this
    // is the one query worth polling.
    refetchInterval: 20_000
  })

export const useResolveApproval = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string
      decision: 'approved' | 'rejected' | 'edited_and_approved'
      digest: string
      editedArguments?: Record<string, unknown>
      note?: string
    }) => apiSend<{ resolved: boolean; runId: string; streamUrl: string }>(`/api/approvals/${id}`, 'POST', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.approvals })
  })
}

/** Statuses the pipeline sets while a document is still being read (PRD-24 D-5). */
const READING_STATUSES: ReadonlySet<string> = new Set(['pending', 'extracting', 'chunking', 'embedding'])

export const isBeingRead = (document: Pick<KnowledgeDocument, 'status'>): boolean =>
  READING_STATUSES.has(document.status)

/** What this deployment can read: whether OCR exists and which languages it carries. */
export const useReadingCapabilities = (enabled = true) =>
  useQuery({
    queryKey: ['knowledge', 'reading'] as const,
    queryFn: () =>
      apiGet<{ pipelineVersion: string; ocr: { engine: 'tesseract' | 'none'; languages: string[] } }>(
        '/api/knowledge/reading'
      ),
    enabled,
    staleTime: 5 * 60_000
  })

/**
 * The `accept` list for a file input: every supported format, minus the image formats when
 * nothing in this deployment can read a picture (PRD-24 D-12). Until the capability answer
 * arrives everything is offered — one refusal beats a format that silently never appears.
 */
export const useUploadAccept = (): string => {
  const reading = useReadingCapabilities()
  const ocrAvailable = reading.data ? reading.data.ocr.engine !== 'none' : true
  return SUPPORTED_UPLOAD_FORMATS.filter((f) => ocrAvailable || !f.needsOcr)
    .flatMap((f) => f.extensions)
    .join(',')
}

export const useMemories = () =>
  useQuery({ queryKey: queryKeys.memories, queryFn: () => apiGet<Page<Memory>>('/api/memories') })

export const useCreateMemory = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { category: Memory['category']; statement: string }) =>
      apiSend<{ id: string }>('/api/memories', 'POST', body),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.memories })
  })
}

export const useUpdateMemory = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string; enabled?: boolean; statement?: string }) =>
      apiSend<{ updated: boolean }>(`/api/memories/${id}`, 'PATCH', patch),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.memories })
  })
}

export const useDeleteMemory = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiSend<{ deleted: boolean }>(`/api/memories/${id}`, 'DELETE'),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.memories })
  })
}

export const useTools = (enabled: boolean) =>
  useQuery({ queryKey: queryKeys.tools, queryFn: () => apiGet<Page<ToolDescriptor>>('/api/admin/tools'), enabled })

export const useModels = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.models,
    queryFn: () => apiGet<{ provider: string; items: ModelRegistryEntry[] }>('/api/admin/models'),
    enabled
  })

export const useUsage = (days: number, enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.usage(days),
    queryFn: () => apiGet<UsageSummary>(`/api/admin/usage?days=${days}`),
    enabled
  })

/**
 * How full the four stores are, right now.
 *
 * Not keyed on the window the rest of this screen uses: storage is a level rather than a flow,
 * and a `?days=` on it would invite a client to render "storage over the last 90 days", which
 * does not exist. Kept fresher than the other admin reads — an operator opens this screen while
 * something is filling up, and a five-minute-old disk reading is the one number that is worth
 * less the older it gets.
 */
export const useStorage = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.storage,
    queryFn: () => apiGet<StorageUsage>('/api/admin/storage'),
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: true
  })

export const useAudit = (enabled: boolean) =>
  useQuery({
    queryKey: queryKeys.audit,
    queryFn: () => apiGet<Page<AuditEvent>>('/api/admin/audit?limit=100'),
    enabled
  })
